import { useState } from 'react';
import { usePersistedState } from '../use-persisted-state';
import { Panel } from '../whoop-panel';
import { Segmented } from '../segmented-control';
import { C } from '../whoop-theme';

interface BuilderInputs {
  time: number;
  effort: string;
  terrain: string;
}

interface WorkoutOutput {
  title: string;
  strain: string;
  blocks: { n: string; text: string }[];
  purpose: string;
  whyToday: string;
  impact: string;
  duration: string;
  warn: boolean;
  warnText: string | null;
}

const CTX = [
  { k: 'Goal', v: 'Sub-3:45 marathon' },
  { k: 'Recovery', v: '72%' },
  { k: 'Yesterday', v: 'Threshold (hard)' },
  { k: 'Tomorrow', v: 'Easy run' },
];

const TYPES = [
  'Easy',
  'Recovery',
  'Long',
  'Progression',
  'Threshold',
  'Tempo',
  'Intervals',
  'Hill repeats',
  'Fartlek',
  'Race-pace',
  'Treadmill',
  'Walk/run',
];

const ADAPT = {
  original: ['6 × 800 m intervals @ 5K pace', '90 s jog recovery'],
  adapted: ['4 × 800 m intervals @ 5K pace', '90 s jog recovery'],
  reason:
    'Recovery is lower than normal and resting HR is elevated. You keep race-specific speed while trimming total high-intensity volume.',
};

const capitalize = (value: string) => (value[0] ?? '').toUpperCase() + value.slice(1);

const TIME_OPTIONS = [25, 35, 45, 60, 75].map((v) => ({ value: v, label: `${v}m` }));
const EFFORT_OPTIONS = ['easy', 'moderate', 'hard'].map((v) => ({
  value: v,
  label: capitalize(v),
}));
const TERRAIN_OPTIONS = ['roads', 'treadmill', 'trail'].map((v) => ({
  value: v,
  label: capitalize(v),
}));

function generate(b: BuilderInputs, variant: number): WorkoutOutput {
  const t = b.time;
  const terrText =
    b.terrain === 'treadmill' ? ' (treadmill)' : b.terrain === 'trail' ? ' on trail' : '';
  const easy = Math.round(t * 0.28);
  const mid = Math.round(t * 0.42);
  const prog = Math.round(t * 0.15);
  const build = Math.round(t * 0.15);

  const sets = {
    easy: {
      title: 'Easy aerobic run' + terrText,
      strain: 'Low',
      blocks: [
        { n: '1', text: `${build} min relaxed build` },
        { n: '2', text: `${t - build - 5} min easy @ 9:10/mi` },
        { n: '3', text: '5 min walk/jog cool-down' },
      ],
      purpose: 'Pure aerobic volume — keeps the base growing without touching fatigue.',
      whyToday: 'Recovery is moderate and you have quality work later this week.',
      impact: 'Adds volume while fully protecting Saturday’s long run.',
    },
    moderate: {
      title: `${t}-min aerobic progression${terrText}`,
      strain: 'Moderate',
      blocks: [
        { n: '1', text: `${easy} min easy` },
        { n: '2', text: `${mid} min steady aerobic @ 8:50/mi` },
        { n: '3', text: `${prog} min moderate progression` },
        { n: '4', text: '5 min easy cool-down' },
      ],
      purpose: 'Maintain aerobic volume and touch goal effort without a true hard session.',
      whyToday:
        'Recovery is moderate and your plan already includes threshold work later this week.',
      impact: 'Supports consistency without compromising the next quality workout.',
    },
    hard: {
      title: `${t}-min threshold intervals${terrText}`,
      strain: 'High',
      blocks: [
        { n: '1', text: '12 min warm-up + 3 strides' },
        { n: '2', text: '2 × 8 min @ threshold (7:35/mi)' },
        { n: '3', text: '2 min easy jog between' },
        { n: '4', text: '8 min cool-down' },
      ],
      purpose: 'Race-specific threshold stimulus to move marathon pace.',
      whyToday:
        'Only choose this if you feel fresh — recovery is below your usual quality range and a hard day is already logged this week.',
      impact: 'High return on pace, but raises the risk of a compromised long run.',
    },
  };

  type SetKey = keyof typeof sets;
  const variants: Record<string, SetKey[]> = {
    easy: ['easy', 'moderate'],
    moderate: ['moderate', 'easy', 'hard'],
    hard: ['hard', 'moderate'],
  };
  const order = variants[b.effort] ?? ['moderate'];
  const pick: SetKey = order[variant % order.length] ?? 'moderate';
  const w = sets[pick];
  const warn = pick === 'hard';
  return {
    ...w,
    duration: `~${t} min`,
    warn,
    warnText: warn
      ? 'This stacks a second hard day inside 48 hours. The system recommends a moderate session instead.'
      : null,
  };
}

export function BuilderPage() {
  const [builder, setBuilder] = usePersistedState<BuilderInputs>('builder', {
    time: 35,
    effort: 'moderate',
    terrain: 'roads',
  });
  const [variant, setVariant] = useState(0);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const update = (patch: Partial<BuilderInputs>) => {
    setBuilder({ ...builder, ...patch });
    setVariant(0);
    setSaveMsg(null);
  };
  const out = generate(builder, variant);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>
          Workout Builder
        </h1>
        <div style={{ fontSize: 14, color: C.text2, marginTop: 4 }}>
          The best workout I can realistically do today — built around recovery, plan and time.
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 18 }}>
        {CTX.map((c) => (
          <div
            key={c.k}
            style={{
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              fontSize: '12.5px',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: '7px 12px',
              background: C.panel,
            }}
          >
            <span style={{ color: C.text3 }}>{c.k}</span>
            <span style={{ fontWeight: 700 }}>{c.v}</span>
          </div>
        ))}
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 18, alignItems: 'start' }}
      >
        <Panel style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 18 }}>Inputs</div>
          <InputLabel>TIME AVAILABLE</InputLabel>
          <div style={{ marginBottom: 18 }}>
            <Segmented
              options={TIME_OPTIONS}
              active={builder.time}
              onSelect={(time) => update({ time })}
            />
          </div>
          <InputLabel>DESIRED EFFORT</InputLabel>
          <div style={{ marginBottom: 18 }}>
            <Segmented
              options={EFFORT_OPTIONS}
              active={builder.effort}
              onSelect={(effort) => update({ effort })}
            />
          </div>
          <InputLabel>TERRAIN</InputLabel>
          <Segmented
            options={TERRAIN_OPTIONS}
            active={builder.terrain}
            onSelect={(terrain) => update({ terrain })}
          />
        </Panel>

        <Panel accent style={{ padding: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: 16,
              gap: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: 10,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: C.accent,
                  marginBottom: 5,
                }}
              >
                Recommended workout
              </div>
              <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em' }}>
                {out.title}
              </div>
            </div>
            <div
              style={{
                textAlign: 'right',
                fontSize: '11.5px',
                color: C.text2,
                fontFamily: "'JetBrains Mono',monospace",
                flex: 'none',
              }}
            >
              {out.duration}
              <br />
              {out.strain} strain
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
            {out.blocks.map((bl) => (
              <div
                key={bl.n}
                style={{
                  display: 'flex',
                  gap: 12,
                  fontSize: 14,
                  color: C.text,
                  alignItems: 'baseline',
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: 11,
                    color: C.accent,
                    minWidth: 14,
                  }}
                >
                  {bl.n}
                </span>
                {bl.text}
              </div>
            ))}
          </div>
          {out.warn && out.warnText && (
            <div
              style={{
                display: 'flex',
                gap: 9,
                padding: '11px 14px',
                background: 'rgba(227,198,115,0.1)',
                border: '1px solid rgba(227,198,115,0.3)',
                borderRadius: 10,
                fontSize: '12.5px',
                color: '#e9d49a',
                lineHeight: 1.45,
                marginBottom: 16,
              }}
            >
              <span style={{ fontWeight: 700 }}>!</span>
              {out.warnText}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              fontSize: 13,
              lineHeight: 1.5,
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 11,
              marginBottom: 18,
            }}
          >
            <div>
              <span style={{ color: C.text2 }}>Purpose — </span>
              {out.purpose}
            </div>
            <div>
              <span style={{ color: C.text2 }}>Why today — </span>
              {out.whyToday}
            </div>
            <div>
              <span style={{ color: C.text2 }}>Expected impact — </span>
              {out.impact}
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
            <button
              type="button"
              onClick={() => setSaveMsg('Added to your plan for Thursday.')}
              className="ww-btn"
              style={{
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                borderRadius: 9,
                padding: '10px 15px',
                border: 'none',
                background: C.accent,
                color: '#1a1330',
              }}
            >
              Add to plan
            </button>
            <button
              type="button"
              onClick={() => setSaveMsg("Replaced today's workout with this session.")}
              className="ww-btn"
              style={{
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: 9,
                padding: '10px 15px',
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.05)',
                color: C.text,
              }}
            >
              Replace today&apos;s workout
            </button>
            <button
              type="button"
              onClick={() => setSaveMsg('Saved as a reusable template.')}
              className="ww-btn"
              style={{
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: 9,
                padding: '10px 15px',
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.05)',
                color: C.text,
              }}
            >
              Save as template
            </button>
            <button
              type="button"
              onClick={() => setVariant((v) => v + 1)}
              className="ww-btn"
              style={{
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: 9,
                padding: '10px 15px',
                border: '1px solid rgba(181,155,255,0.35)',
                background: 'transparent',
                color: C.accent,
              }}
            >
              ↻ Generate another
            </button>
          </div>
          {saveMsg && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginTop: 14,
                padding: '10px 14px',
                background: 'rgba(127,209,163,0.1)',
                border: '1px solid rgba(127,209,163,0.3)',
                borderRadius: 10,
                fontSize: 13,
                color: '#a9e3c4',
              }}
            >
              <span style={{ fontWeight: 700 }}>✓</span>
              <span style={{ flex: 1 }}>{saveMsg}</span>
              <span
                onClick={() => setSaveMsg(null)}
                style={{
                  cursor: 'pointer',
                  color: C.text2,
                  textDecoration: 'underline',
                  fontSize: 12,
                }}
              >
                dismiss
              </span>
            </div>
          )}
        </Panel>
      </div>

      {/* Adapt existing */}
      <Panel style={{ padding: '22px 24px', marginTop: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
          Or adapt a planned workout
        </div>
        <div style={{ fontSize: '12.5px', color: C.text2, marginBottom: 18 }}>
          The builder can also trim what&apos;s already scheduled instead of making something new.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div
            style={{
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 12,
              padding: 16,
              opacity: 0.7,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontFamily: "'JetBrains Mono',monospace",
                color: C.text3,
                marginBottom: 10,
              }}
            >
              ORIGINAL
            </div>
            {ADAPT.original.map((o) => (
              <div key={o} style={{ fontSize: '13.5px', color: '#cbc9c2', marginBottom: 5 }}>
                {o}
              </div>
            ))}
          </div>
          <div
            style={{
              border: '1px solid rgba(181,155,255,0.3)',
              borderRadius: 12,
              padding: 16,
              background: 'rgba(181,155,255,0.05)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontFamily: "'JetBrains Mono',monospace",
                color: C.accent,
                marginBottom: 10,
              }}
            >
              ADAPTED
            </div>
            {ADAPT.adapted.map((a) => (
              <div key={a} style={{ fontSize: '13.5px', color: C.text, marginBottom: 5 }}>
                {a}
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.5 }}>
          <span style={{ color: '#cbc9c2', fontWeight: 600 }}>Why — </span>
          {ADAPT.reason}
        </div>
      </Panel>

      {/* Type palette */}
      <div style={{ marginTop: 20 }}>
        <div
          style={{
            fontSize: 12,
            color: C.text3,
            marginBottom: 10,
            fontFamily: "'JetBrains Mono',monospace",
            letterSpacing: '0.08em',
          }}
        >
          WORKOUT TYPES IN THE LIBRARY
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {TYPES.map((ty) => (
            <span
              key={ty}
              style={{
                fontSize: '12.5px',
                color: '#cbc9c2',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 999,
                padding: '6px 13px',
                background: 'rgba(255,255,255,0.03)',
              }}
            >
              {ty}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function InputLabel({ children }: { children: string }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: C.text3,
        marginBottom: 8,
        fontFamily: "'JetBrains Mono',monospace",
        letterSpacing: '0.08em',
      }}
    >
      {children}
    </div>
  );
}
